import React, { useEffect, useState, MouseEvent } from 'react';
import classNames from 'classnames';
import { useDispatch } from "react-redux";
import Spinner from '../../common/Spinner/Spinner';
import Message from '../../common/Message/Message';
import { formatTranslatedText } from '../../common/util/Functions';
import { useTranslation, useOverview } from '../../common/util/Hook';
import { BankIdInfoEntry, DateAsString } from '../../generated-types';
import { getBankIdInfo } from '../../actions/overview';
import { StringObject } from '../../types';

import './BankIdInfo.scss';

interface BankIdData {
    warning: string;
    front: string[][];
    back: string[][];
}

const getDateString = (date?: DateAsString | null): string => {
    const text = `${date || ''}`;
    const splited = text.split('-');
    return splited.length > 1 ? splited.reverse().join('.') : (text || '');
};

// Distinguished name example: "CN=Ola Nordman,serialNumber=9578-5992-1-2025,OU=8016,O=BBS\,123456789,O=BankID - Devlab,C=NO"
// We insert a newline after not-escaped comma and remove the escape character before the escaped comma
const formatDName = (str: string): string =>
    str.replace(/\\,/gi, '%%%').replace(/,/gi, '\n').replace(/%%%/gi, ',')

const convertBankIdInfoToData = ( translated: StringObject, info: BankIdInfoEntry): BankIdData => {
    const now = new Date();
    const expired = info.validNotAfter ? new Date( info.validNotAfter ) : null;
    const diffDay = !expired ? 0 : Math.ceil(((expired.getTime() - now.getTime()) / (1000*60*60*24)));
    const dayUnit = diffDay > 1 ? translated.bankIdInfoDager : translated.bankIdInfoDag;

    return {
        warning: diffDay > 0 && diffDay < 31 ? formatTranslatedText([diffDay, dayUnit], true, translated.bankIdInfoUtloper) : '',
        front: [
            [translated.bankIdInfoGyldig, [getDateString(info.validNotBefore), getDateString(info.validNotAfter)].join(' - ').trim() ],
            [translated.bankIdInfoRegistrert, getDateString(info.registered) ],
            [translated.bankIdInfoEndret, getDateString(info.statusChanged) ],
        ],
        back: [
            [translated.bankIdInfoEmne, formatDName(info.subject || '')],
            [translated.bankIdInfoUtsteder, formatDName(info.issuer || '')],
        ].filter( (text: string[]) => !!text[1]),
    }
};

// eslint-disable-next-line
export default (): React.ReactElement => {
    const dispatch = useDispatch();
    const translated = useTranslation();
    const { bankIdInfo } = useOverview();
    const { active, revoked } = bankIdInfo || {};
    const [openDetail, setOpenDetail] = useState<undefined | number>();

    useEffect( () => {
        if ( bankIdInfo !== undefined ) { return; }
            // @ts-ignore
        dispatch(getBankIdInfo());
    }, [bankIdInfo, dispatch]);

    return (
        <div className={classNames('bankId-info-wrapper')}>
            <h1 className="paragraph">{translated.bankIdInfoTittel}</h1>

            { bankIdInfo === undefined && <Spinner /> }
            { bankIdInfo !== undefined && !revoked && !active && 
                <Message type="error-page" text={translated.opsNoeGikkGalt} /> 
            }

            { bankIdInfo !== undefined && (revoked || active) && ((revoked || []).length + (active || []).length) === 0 &&
                <Message type="empty" text={translated.bankIdInfoTomt} /> 
            }

            { bankIdInfo !== undefined && ((revoked || []).length + (active || []).length) > 0 && 
                <ul className="bankId-info-list">
                   { (active || []).concat((revoked || [])).map( (info: BankIdInfoEntry, i: number) => {
                        const key = `active-bankId-info-${i}`;
                        const data = convertBankIdInfoToData( translated, info );
                        return <li key={key} className={classNames('bankIdInfo-item ', {
                                '-open-detail':  i === openDetail,
                                '-mobile': !!info.mobilephone,
                                '-has-warning': !!data.warning,
                                '-employee': /(ansatt|employee)/i.test( info.type ),
                            })}>
                            <div className="item-content">
                                <div className="status-holder">
                                    <span className="info-status">{info.orderstatus}</span>
                                    { !!data.warning && <span className="warning-status">{data.warning}</span> }
                                </div>

                                <div className="front-side">
                                    <div className="left-content">
                                        <div className="info-image" aria-hidden="true"/>
                                        <div className="info-content">
                                            <div className="info-type" aria-label={`${translated.bankIdInfoType}: ${info.type}`}>
                                                {info.type}
                                            </div>
                                            { !!info.mobilephone && <div className="info-mobile" aria-label={`${translated.bankIdInfoMobil}: ${info.mobilephone}`}>{info.mobilephone}</div> }
                                            <div className="info-originator" aria-label={`${translated.bankIdInfoBank}: ${info.originator}`}>{info.originator}</div>
                                        </div>
                                    </div>

                                    <div className="right-content">
                                        { data.front.map( (text: string[], j: number) => (
                                            <div key={`${key}-front-${j}`} className="info-row">
                                                <span id={`${key}-front-${j}`} >{text[0]}</span>
                                                <span aria-labelledby={`${key}-front-${j}`}>{text[1]}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                { (data.back.length > 0 || (info.possesionElements || []).length > 0) && <div className="back-side">
                                    <div className="back-content">
                                        { (info.possesionElements || []).length > 0 && <div className="info-row">
                                            <span id={`${key}-back-element`}>{translated.bankIdInfoElementer}</span>
                                            <ul className="info-element-list">
                                                { info.possesionElements.map( (text: string, j: number) => (
                                                    <li key={`${key}-back-element-${j}`} aria-labelledby={`${key}-back-element`}>{text}</li>
                                                )) }
                                            </ul>
                                        </div>}

                                        { data.back.map( (text: string[], j: number) => (
                                            <div key={`${key}-back-${j}`} className="info-row">
                                                <span id={`${key}-back-${j}`} >{text[0]}</span>
                                                <span aria-labelledby={`${key}-back-${j}`}>{text[1]}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="back-tool">
                                        <a 
                                            href="#" 
                                            role="button" 
                                            className="detail-toggler-btn bank-id-link" 
                                            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                                                e.preventDefault();
                                                setOpenDetail((openDetail === i ? undefined : i))
                                            }}
                                        >{ i === openDetail ? translated.visMindre : translated.visMer }</a>
                                    </div>
                                </div> }
                            </div>
                        </li>
                   })}
                </ul>
            }
        </div>
    );
};
